import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
    IActivityStream,
    ICreateIroNamRequest,
    IIroNamRequest,
    RequestState,
    ServiceName
} from '@shs-digi-care-monorepo/interfaces';

export type CreateIroNamCustomerRequest = Omit<ICreateIroNamRequest, 'createdBy' | 'name' | 'email' | 'isInternal'>;
import { AssignedToMyTeamsFilterState, FilterOptions } from '../models/filter.model';
import { environment, Paginated, toRequestParams } from '@shs-digi-care-monorepo/frontend-lib';

/**
 * Service for managing IRO requests via API.
 */
@Injectable({
    providedIn: 'root'
})
export class RequestService {
    protected requestEndpoint = 'iro-nam-requests';
    private readonly httpClient = inject(HttpClient);

    /**
     * Creates a new IRO request.
     * @param payload The request data
     * @returns Observable of the created request
     */
    public createRequest(payload: CreateIroNamCustomerRequest): Observable<IIroNamRequest> {
        return this.httpClient.post<IIroNamRequest>(`${environment.apiUrl}/${this.requestEndpoint}`, {
            ...payload,
            additionalEmails: payload.additionalEmails?.filter((email: string) => Boolean(email)) ?? []
        });
    }

    /**
     * Updates an existing IRO request.
     * @param id The request ID to update
     * @param updates Partial request data to update
     * @returns Observable of the updated request
     */
    public updateRequest(id: string, updates: Partial<IIroNamRequest>): Observable<IIroNamRequest> {
        return this.httpClient.patch<IIroNamRequest>(`${environment.apiUrl}/${this.requestEndpoint}/${id}`, updates);
    }

    /**
     * Retrieves a request by its ID.
     * @param id The request ID
     * @returns Observable of the request data
     */
    public getRequestById(id: string): Observable<IIroNamRequest> {
        return this.httpClient.get<IIroNamRequest>(`${environment.apiUrl}/${this.requestEndpoint}/${id}`);
    }

    /**
     * Deletes a request by its ID.
     * @param id The request ID to delete
     * @returns Observable of void
     */
    public deleteRequest(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${environment.apiUrl}/${this.requestEndpoint}/${id}`);
    }

    /**
     * Gets requests assigned to the teams of the current user.
     */
    public getAssignedToMyTeams(filters: AssignedToMyTeamsFilterState): Observable<Paginated<IIroNamRequest>> {
        return this.httpClient.get<Paginated<IIroNamRequest>>(
            `${environment.apiUrl}/iro-nam-requests/assigned-to-my-teams`,
            {
                params: toRequestParams(filters)
            }
        );
    }

    /**
     * Gets requests assigned to the teams of the current user.
     */
    public getAssignedToMyTeamsFilterOptions(states: RequestState[]): Observable<FilterOptions> {
        return this.httpClient.get<FilterOptions>(
            `${environment.apiUrl}/iro-nam-requests/assigned-to-my-teams/filters`,
            {
                params: {
                    status: states.join(',')
                }
            }
        );
    }

    public getMyRequests(filters: AssignedToMyTeamsFilterState): Observable<Paginated<IIroNamRequest>> {
        return this.httpClient.get<Paginated<IIroNamRequest>>(`${environment.apiUrl}/iro-nam-requests/my`, {
            params: toRequestParams(filters)
        });
    }

    public getMyResponseNeededRequests(filters: AssignedToMyTeamsFilterState): Observable<Paginated<IIroNamRequest>> {
        return this.httpClient.get<Paginated<IIroNamRequest>>(
            `${environment.apiUrl}/iro-nam-requests/my/response-needed`,
            {
                params: toRequestParams(filters)
            }
        );
    }

    public getUsernamesByGIDs(gids: string[]): Observable<{ gid: string; firstName: string; lastName: string }[]> {
        return this.httpClient.get<{ gid: string; firstName: string; lastName: string }[]>(
            `${environment.apiUrl}/users/usernames`,
            { params: { gids: gids.join(',') } }
        );
    }

    public getActivityStream(requestId: string, page: number, size: number): Observable<Paginated<IActivityStream>> {
        return this.httpClient.get<Paginated<IActivityStream>>(
            `${environment.apiUrl}/activity-streams/requests/${requestId}/activities`,
            {
                params: {
                    page: page.toString(),
                    pageSize: size.toString(),
                    service: ServiceName.IroNamService
                }
            }
        );
    }
}
